// Shared per-platform setup-instructions block. Used by AddReceiver and
// ReceiverDetail so the two paths can't drift. Mirrors the
// platform-tab content on the Downloads page (Downloads.js) — keep the
// instructions in sync if you change one.
//
// Props:
//   software   one of "aviotrack-windows" | "aviotrack-mac" | "aviotrack-linux"
//                | "readsb" | "dump1090" | "dump1090-fa"
//   uuid       the receiver UUID to substitute into copyable commands
//   onCopy     optional callback invoked with the copied string
import React from 'react';

const COPY_ICON = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 2a2 2 0 0 1 2-2h4.586a1 1 0 0 1 .707.293l2.414 2.414a1 1 0 0 1 .293.707V11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V2z" stroke="currentColor" strokeWidth="1" fill="none"/>
    <path d="M2 6h2v7a1 1 0 0 0 1 1h5v2H5a2 2 0 0 1-2-2V6z" fill="currentColor"/>
  </svg>
);

function CopyableCommand({ text, onCopy, title }) {
  const handle = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
    if (onCopy) onCopy(text);
  };
  return (
    <div className="command-block">
      <span className="command">{text}</span>
      <button type="button" className="btn-copy" onClick={handle} title={title || 'Copy'}>
        {COPY_ICON}
      </button>
    </div>
  );
}

function Step({ children }) {
  return <li><div className="instruction-item">{children}</div></li>;
}

function Heading({ software }) {
  const labels = {
    'aviotrack-windows': 'Aviotrack Windows Receiver',
    'aviotrack-mac': 'Aviotrack macOS Receiver',
    'aviotrack-linux': 'Aviotrack Linux Receiver',
    'readsb': 'readsb',
    'dump1090': 'dump1090',
    'dump1090-fa': 'dump1090-fa',
  };
  return <label>Setup Instructions - {labels[software] || software}</label>;
}

export default function SetupInstructions({ software, uuid, onCopy, onNavigate = null }) {
  // The app has no URL routing, so "/?page=downloads" just reloaded onto the
  // map. Navigate in-app when we can; keep the href for open-in-new-tab.
  const goDownloads = (tab) => (e) => {
    if (!onNavigate) return;
    e.preventDefault();
    onNavigate('downloads', { tab });
  };
  if (!software) return null;
  const u = uuid || '<your-uuid>';

  if (software === 'aviotrack-windows') {
    return (
      <div className="config-instructions">
        <Heading software={software} />
        <div className="instructions-content">
          <ol>
            <Step>
              <span className="instruction-text">
                Download <strong>AviotrackReceiver-&lt;version&gt;-Setup.exe</strong> from the{' '}
                <a href="/?page=downloads" onClick={goDownloads('install-windows')} target="_blank" rel="noopener noreferrer">Downloads page</a>{' '}
                and run the installer on your Windows PC.
              </span>
            </Step>
            <Step>
              <span className="instruction-text">
                Connect your RTL-SDR or Airspy device. Windows will prompt to install the WinUSB
                driver via Zadig — accept.
              </span>
            </Step>
            <Step>
              <span className="instruction-text">
                Launch <strong>Aviotrack Receiver</strong> from the Start Menu, open{' '}
                <strong>Settings → Server</strong>, paste your receiver UUID, and click <strong>Save</strong>:
              </span>
              <CopyableCommand text={u} onCopy={onCopy} title="Copy UUID" />
            </Step>
            <Step>
              <span className="instruction-text">
                You should see your receiver come online here within seconds. Most settings can
                also be edited live from this page after that.
              </span>
            </Step>
          </ol>
        </div>
      </div>
    );
  }

  if (software === 'aviotrack-mac') {
    return (
      <div className="config-instructions">
        <Heading software={software} />
        <div className="instructions-content">
          <ol>
            <Step>
              <span className="instruction-text">
                Download <strong>AviotrackReceiver-&lt;version&gt;.dmg</strong> from the{' '}
                <a href="/?page=downloads" onClick={goDownloads('install-mac')} target="_blank" rel="noopener noreferrer">Downloads page</a>,
                open it, and drag <strong>Aviotrack Receiver</strong> to your Applications folder.
                On first launch, right-click the app and choose <strong>Open</strong> to bypass
                Gatekeeper.
              </span>
            </Step>
            <Step>
              <span className="instruction-text">
                Install SDR drivers via Homebrew (skip if you only use a Beast/Hulc serial device):
              </span>
              <CopyableCommand text="brew install librtlsdr airspy" onCopy={onCopy} />
            </Step>
            <Step>
              <span className="instruction-text">
                Open the app, go to <strong>Settings → Server</strong>, paste the UUID, and click
                <strong> Save</strong>:
              </span>
              <CopyableCommand text={u} onCopy={onCopy} title="Copy UUID" />
            </Step>
            <Step>
              <span className="instruction-text">
                The app auto-detects your USB device and starts feeding. Most settings can be
                edited live from this page after that.
              </span>
            </Step>
          </ol>
        </div>
      </div>
    );
  }

  if (software === 'aviotrack-linux') {
    const installCmd = 'sudo bash -c "$(wget -O - https://aviotrack.com/pi-install.sh)"';
    const setupCmd = `sudo aviotrack-setup ${u}`;
    return (
      <div className="config-instructions">
        <Heading software={software} />
        <div className="instructions-content">
          <ol>
            <Step>
              <span className="instruction-text">
                On your Pi or Linux box, run the one-line installer. It pulls Java 21, RTL-SDR /
                Airspy drivers, sets up udev rules, and installs a headless systemd service:
              </span>
              <CopyableCommand text={installCmd} onCopy={onCopy} />
            </Step>
            <Step>
              <span className="instruction-text">
                Configure the receiver UUID. The service restarts automatically:
              </span>
              <CopyableCommand text={setupCmd} onCopy={onCopy} title="Copy setup command" />
            </Step>
            <Step>
              <span className="instruction-text">
                The receiver auto-detects RTL-SDR / Airspy / GNS Hulc devices and any existing
                decoders (readsb, dump1090). View live logs with{' '}
                <code>sudo journalctl -u aviotrack-receiver -f</code>.
              </span>
            </Step>
            <Step>
              <span className="instruction-text">
                To uninstall later: <code>sudo aviotrack-uninstall</code>.
              </span>
            </Step>
          </ol>
        </div>
      </div>
    );
  }

  if (software === 'readsb') {
    return (
      <div className="config-instructions">
        <Heading software={software} />
        <div className="instructions-content">
          <ol>
            <Step>
              <span className="instruction-text">Open a terminal connection to your Pi.</span>
            </Step>
            <Step>
              <span className="instruction-text">Edit the readsb config file:</span>
              <CopyableCommand text="sudo nano /etc/default/readsb" onCopy={onCopy} />
            </Step>
            <Step>
              <span className="instruction-text">Add this block to the <code>RECEIVER_OPTIONS</code> line:</span>
              <CopyableCommand
                text={`--net-connector feeders.aviotrack.com,30009,beast_reduce_plus_out,uuid=${u}`}
                onCopy={onCopy}
              />
            </Step>
            <Step>
              <span className="instruction-text">Save (Ctrl-X, Y) and restart readsb:</span>
              <CopyableCommand text="sudo systemctl restart readsb" onCopy={onCopy} />
            </Step>
            <Step>
              <span className="instruction-text">
                Your receiver should appear online here within seconds.
              </span>
            </Step>
          </ol>
        </div>
      </div>
    );
  }

  if (software === 'dump1090' || software === 'dump1090-fa') {
    return (
      <div className="config-instructions">
        <Heading software={software} />
        <div className="instructions-content">
          <p>
            Add a Beast-out connection to {software}'s startup arguments and point it at{' '}
            <strong>feeders.aviotrack.com:30009</strong>. The exact arg syntax varies by
            distribution; for{' '}
            <a href="https://github.com/wiedehopf/readsb" target="_blank" rel="noopener noreferrer">
              wiedehopf/readsb
            </a>{' '}
            (recommended over dump1090 for feeders) follow the readsb instructions in the
            dropdown above. Use this UUID:
          </p>
          <CopyableCommand text={u} onCopy={onCopy} title="Copy UUID" />
        </div>
      </div>
    );
  }

  return null;
}
